// Signature boxes the sender drops on a document in the send window. Each box
// is an Apryse FreeText annotation tagged with custom data naming its type and
// signer; on Done the annotations are read back as PDF coordinates for the
// backend (see utils/esignBoxPlacement).

import { getAnnotationPdfPlacement } from '../../utils/esignBoxPlacement';

export const BOX_DATA_KEY = 'whgEsignBox';

export const BOX_TYPES = [
  { type: 'signature', label: 'Signature', width: 160, height: 36 },
  { type: 'initial', label: 'Initials', width: 70, height: 30 },
  { type: 'date', label: 'Date', width: 110, height: 24 },
  { type: 'fullname', label: 'Name', width: 170, height: 24 },
  { type: 'text', label: 'Text', width: 170, height: 24 },
];

// One colour per signer position, matching the recipient chips.
export const SIGNER_COLORS = ['#2563EB', '#16A34A', '#D97706', '#9333EA', '#DC2626'];

export const signerColor = (position) => SIGNER_COLORS[(Math.max(1, position) - 1) % SIGNER_COLORS.length];

const typeInfo = (type) => BOX_TYPES.find((box) => box.type === type) || BOX_TYPES[0];

export const boxLabel = (type, signerName) =>
  `${typeInfo(type).label} — ${signerName || 'Signer'}`;

const rgba = (Annotations, hex, alpha = 1) => {
  const value = parseInt(hex.slice(1), 16);
  return new Annotations.Color((value >> 16) & 255, (value >> 8) & 255, value & 255, alpha);
};

const readBoxData = (annotation) => {
  try {
    const raw = annotation?.getCustomData?.(BOX_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const isPlacedBox = (annotation) => Boolean(readBoxData(annotation));

export const createBoxAnnotation = (instance, { type, signerPosition, signerEmail, signerName }, placement = null) => {
  const { Annotations, annotationManager, documentViewer } = instance.Core;
  const info = typeInfo(type);
  const pageNumber = placement?.pageNumber || documentViewer.getCurrentPage?.() || 1;
  const pageInfo = documentViewer.getDocument()?.getPageInfo?.(pageNumber) || { width: 612, height: 792 };

  const annotation = new Annotations.FreeTextAnnotation();
  annotation.PageNumber = pageNumber;
  annotation.Width = placement?.width || info.width;
  annotation.Height = placement?.height || info.height;
  annotation.X = placement?.x ?? Math.max(0, pageInfo.width / 2 - annotation.Width / 2);
  annotation.Y = placement?.y ?? Math.max(0, pageInfo.height / 2 - annotation.Height / 2);
  const color = signerColor(signerPosition);
  annotation.FillColor = rgba(Annotations, color, 0.12);
  annotation.StrokeColor = rgba(Annotations, color);
  annotation.TextColor = rgba(Annotations, color);
  annotation.StrokeThickness = 1;
  annotation.FontSize = '10pt';
  annotation.Author = annotationManager.getCurrentUser?.() || annotation.Author;
  annotation.setContents(boxLabel(type, signerName));
  annotation.setCustomData(BOX_DATA_KEY, JSON.stringify({
    field_type: type,
    signer_position: signerPosition,
    signer_email: signerEmail || '',
  }));
  annotationManager.addAnnotation(annotation);
  annotationManager.redrawAnnotation(annotation);
  return annotation;
};

// A box centred on a page point, kept inside the page.
export const boxPlacementAt = (instance, type, { pageNumber, x, y }) => {
  const info = typeInfo(type);
  const page = instance.Core.documentViewer.getDocument()?.getPageInfo?.(pageNumber) || { width: 612, height: 792 };
  const clamp = (value, max) => Math.min(Math.max(0, value), Math.max(0, max));
  return {
    pageNumber,
    width: info.width,
    height: info.height,
    x: clamp(x - info.width / 2, page.width - info.width),
    y: clamp(y - info.height / 2, page.height - info.height),
  };
};

// The viewer clears a selection made in the same moment a box is added, so
// select on the next tick; the box is then ready to drag or resize at once.
export const selectBoxSoon = (instance, annotation) => {
  setTimeout(() => {
    const { annotationManager } = instance.Core;
    if (!annotationManager.getAnnotationsList?.().includes(annotation)) return;
    annotationManager.deselectAllAnnotations?.();
    annotationManager.selectAnnotation?.(annotation);
  }, 30);
};

export const placedBoxAnnotations = (instance) =>
  (instance?.Core?.annotationManager?.getAnnotationsList?.() || []).filter(isPlacedBox);

// Annotations -> the boxes sent with the request.
export const readPlacedBoxes = (instance) =>
  placedBoxAnnotations(instance)
    .map((annotation) => {
      const data = readBoxData(annotation);
      const placement = getAnnotationPdfPlacement(instance, annotation);
      if (!data || !placement?.width || !placement?.height) return null;
      return {
        field_type: data.field_type,
        signer_position: data.signer_position,
        signer_email: data.signer_email,
        page_number: placement.page_number,
        x: placement.x,
        y: placement.y,
        width: placement.width,
        height: placement.height,
        required: true,
      };
    })
    .filter(Boolean);

// Boxes -> annotations again, when the sender reopens the placement screen.
export const restorePlacedBoxes = (instance, boxes, recipients) => {
  const doc = instance?.Core?.documentViewer?.getDocument?.();
  (boxes || []).forEach((box) => {
    const toViewer = doc?.getViewerCoordinates?.bind(doc);
    const topLeft = toViewer ? toViewer(box.page_number, box.x, box.y) : { x: box.x, y: box.y };
    const recipient = recipients[box.signer_position - 1];
    createBoxAnnotation(
      instance,
      {
        type: box.field_type,
        signerPosition: box.signer_position,
        signerEmail: box.signer_email,
        signerName: recipient?.name,
      },
      { pageNumber: box.page_number, x: topLeft.x, y: topLeft.y, width: box.width, height: box.height },
    );
  });
};

const sameEmail = (a, b) => Boolean(a) && (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

// The live annotations that belong to one signer (by email, else by position).
export const boxAnnotationsForSigner = (instance, recipient, position) =>
  placedBoxAnnotations(instance).filter((annotation) => {
    const data = readBoxData(annotation) || {};
    if (data.signer_email && recipient?.email) return sameEmail(data.signer_email, recipient.email);
    return Number(data.signer_position) === Number(position);
  });

// After people are added, removed or reordered on the placement screen, each
// box's number, colour and label must follow its signer.
export const resyncBoxesToRecipients = (instance, recipients) => {
  const { Annotations, annotationManager } = instance?.Core || {};
  if (!Annotations || !annotationManager) return;
  placedBoxAnnotations(instance).forEach((annotation) => {
    const data = readBoxData(annotation);
    if (!data) return;
    const index = recipients.findIndex((recipient) => sameEmail(data.signer_email, recipient.email));
    if (index < 0) return;
    const position = index + 1;
    const color = signerColor(position);
    annotation.FillColor = rgba(Annotations, color, 0.12);
    annotation.StrokeColor = rgba(Annotations, color);
    annotation.TextColor = rgba(Annotations, color);
    annotation.setContents(boxLabel(data.field_type, recipients[index]?.name));
    annotation.setCustomData(BOX_DATA_KEY, JSON.stringify({ ...data, signer_position: position }));
    annotationManager.redrawAnnotation(annotation);
  });
};

// Boxes whose signer is no longer on the recipient list.
export const orphanedBoxes = (boxes, recipients) => {
  const emails = new Set(recipients.map((r) => (r.email || '').trim().toLowerCase()).filter(Boolean));
  return (boxes || []).filter((box) => {
    const email = (box.signer_email || '').trim().toLowerCase();
    if (email) return !emails.has(email);
    return box.signer_position > recipients.length;
  });
};

// Keep each box's position in step with its signer after rows are reordered.
export const alignBoxesToRecipients = (boxes, recipients) => {
  const emails = recipients.map((r) => (r.email || '').trim().toLowerCase());
  return (boxes || []).map((box) => {
    const email = (box.signer_email || '').trim().toLowerCase();
    const index = email ? emails.indexOf(email) : -1;
    return index >= 0 ? { ...box, signer_position: index + 1 } : box;
  });
};
